// A multi-version concurrency-control (MVCC) key-value store.
//
// Memory is modeled as a flat map from addresses to objects. A Set is not one
// object: it is a namespace marker plus one child key per member, and — like the
// Go backend's per-entry cells — each member's key is derived from the ELEMENT's
// identity (its address), so add / remove / contains each touch exactly one key:
//
//     /1        => Player{...}
//     /2        => Player{...}
//     /3        => Bullet{...}
//     /4        => Set<Player>      (a set marker; never rewritten after creation)
//     /4/1      => ∈ /1             (membership of element /1: key = /4 + "/1")
//     /4/2      => ∈ /2
//
// Every address keeps a chain of *versions*. Each version carries the commit
// id (`begin`) that created it and an optional `end` commit id at which it was
// superseded or deleted. A reader with snapshot version `v` sees the version
// whose `begin <= v < end` — i.e. the newest version committed at or before the
// snapshot. This is what makes transactions see a stable snapshot of memory.

import { Address, StoredObject, storedTypeName } from "./value.js";

export interface VersionedEntry {
  /** Commit id of the transaction that created this version. */
  begin: number;
  /** Commit id at which this version was superseded/deleted; null == still live. */
  end: number | null;
  /** null == tombstone (address was deleted at `begin`). */
  value: StoredObject | null;
}

export class Store {
  private chains = new Map<Address, VersionedEntry[]>();
  private nextObjectId = 1;

  /** The id of the most recently committed transaction (monotonic). */
  committedVersion = 0;

  /** Allocate a fresh top-level object address. */
  allocAddress(): Address {
    return `/${this.nextObjectId++}`;
  }

  /** The next commit version a committing transaction should claim. */
  nextCommitVersion(): number {
    return this.committedVersion + 1;
  }

  /**
   * Read the version of `addr` visible at snapshot `readVersion`.
   * Returns undefined if the address does not exist (or is a tombstone) there.
   */
  read(addr: Address, readVersion: number): VersionedEntry | undefined {
    const chain = this.chains.get(addr);
    if (!chain) return undefined;
    // Newest-first: the last entry whose begin <= readVersion and not yet ended.
    for (let i = chain.length - 1; i >= 0; i--) {
      const e = chain[i]!;
      if (e.begin <= readVersion && (e.end === null || e.end > readVersion)) {
        return e.value === null ? undefined : e;
      }
    }
    return undefined;
  }

  /**
   * The begin id of the currently-live (latest committed) version of `addr`,
   * or 0 if the address has never existed. Used for optimistic validation: a
   * read is stale if this is greater than the reader's snapshot version.
   */
  latestBegin(addr: Address): number {
    const chain = this.chains.get(addr);
    if (!chain || chain.length === 0) return 0;
    return chain[chain.length - 1]!.begin;
  }

  /**
   * The begin id of the newest version of `addr` created at or before `asOf`,
   * or 0 if the address had no version by then. Like `latestBegin` but bounded
   * to a point in time: used to detect, *as of a past snapshot*, whether a
   * transaction's read has since been overwritten by a committed write — the
   * point-in-time form of `Transaction.validate`, for the debugger's graph view.
   */
  latestBeginAsOf(addr: Address, asOf: number): number {
    const chain = this.chains.get(addr);
    if (!chain) return 0;
    // Chains are append-only in commit order, so the newest version with
    // begin <= asOf is the last one that qualifies.
    for (let i = chain.length - 1; i >= 0; i--) {
      const begin = chain[i]!.begin;
      if (begin <= asOf) return begin;
    }
    return 0;
  }

  /**
   * Commit a single write at `commitVersion`: close out the current live
   * version (if any) and append a new one. `value === null` writes a tombstone
   * (deletion). Callers must pass `commitVersion === committedVersion + 1` and
   * then advance `committedVersion` once all writes in the transaction land
   * (see `commit`).
   */
  applyWrite(addr: Address, value: StoredObject | null, commitVersion: number): void {
    let chain = this.chains.get(addr);
    if (!chain) {
      chain = [];
      this.chains.set(addr, chain);
    }
    const live = chain[chain.length - 1];
    if (live && live.end === null) {
      live.end = commitVersion;
    }
    chain.push({ begin: commitVersion, end: null, value });
  }

  /** Atomically apply a batch of writes and advance the committed version. */
  commit(writes: Map<Address, StoredObject | null>): number {
    const commitVersion = this.nextCommitVersion();
    for (const [addr, value] of writes) {
      this.applyWrite(addr, value, commitVersion);
    }
    this.committedVersion = commitVersion;
    return commitVersion;
  }

  /**
   * All addresses that are direct children of `prefix` (e.g. set entries under
   * `/4`) and live at `readVersion`. Prefix should not have a trailing slash.
   */
  liveChildren(prefix: Address, readVersion: number): Address[] {
    const needle = prefix + "/";
    const out: Address[] = [];
    for (const addr of this.chains.keys()) {
      if (!addr.startsWith(needle)) continue;
      // direct child only (no further slashes)
      if (addr.indexOf("/", needle.length) !== -1) continue;
      if (this.read(addr, readVersion)) out.push(addr);
    }
    return out;
  }

  /** All live addresses at a given version (for debugging / snapshots). */
  snapshot(readVersion: number = this.committedVersion): Map<Address, StoredObject> {
    const out = new Map<Address, StoredObject>();
    for (const addr of this.chains.keys()) {
      const e = this.read(addr, readVersion);
      if (e && e.value) out.set(addr, e.value);
    }
    return out;
  }

  /**
   * A best-effort type name for every address that has ever held an object,
   * taken from any version in its chain (so even deleted objects are typed).
   * Structs map to their declared type, collections to their container kind.
   * Used by the trace view to give keys a type (`/player/2`) and to group key
   * lifelines by type.
   */
  objectTypes(): Map<Address, string> {
    const out = new Map<Address, string>();
    for (const [addr, chain] of this.chains) {
      for (const e of chain) {
        if (e.value) {
          out.set(addr, storedTypeName(e.value));
          break;
        }
      }
    }
    return out;
  }

  /** Total number of stored versions across all addresses (for stats). */
  versionCount(): number {
    let n = 0;
    for (const chain of this.chains.values()) n += chain.length;
    return n;
  }
}
